import { Alert, Box, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { CommaListField, SectionLabel, StringListEditor } from './primitives'
import type {
  ErrorCorrectionContent,
  OpenAnswerContent,
  OralAnswerContent,
  TranslationContent,
} from '../contentTypes'

/**
 * Редакторы текстовых типов: поиск ошибки, перевод, письменный и устный
 * свободный ответ.
 */

type EditorProps<T> = {
  content: T
  onChange: (content: T) => void
  disabled?: boolean
}

export function ErrorCorrectionEditor({ content, onChange, disabled }: EditorProps<ErrorCorrectionContent>) {
  const { t } = useTranslation()
  const errorIndex = content.error_index ?? 0

  return (
    <Stack spacing={2}>
      <CommaListField
        label={t('exercises.editor.tokens')}
        helperText={t('exercises.editor.tokensHint')}
        values={content.tokens}
        disabled={disabled}
        onChange={(tokens) =>
          onChange({
            ...content,
            tokens,
            // Ошибочное слово могло уехать за пределы предложения
            error_index: Math.min(errorIndex, Math.max(tokens.length - 1, 0)),
          })
        }
      />

      <Box>
        <SectionLabel text={t('exercises.editor.errorToken')} hint={t('exercises.editor.errorTokenHint')} />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {content.tokens.map((token, index) => (
            <Chip
              key={index}
              label={token}
              color={index === errorIndex ? 'error' : 'default'}
              variant={index === errorIndex ? 'filled' : 'outlined'}
              disabled={disabled}
              onClick={() => onChange({ ...content, error_index: index })}
            />
          ))}
        </Stack>
        {content.tokens.length === 0 && <Alert severity="info">{t('exercises.editor.tokensEmpty')}</Alert>}
      </Box>

      <TextField
        select
        size="small"
        label={t('exercises.editor.errorMode')}
        value={content.mode}
        disabled={disabled}
        onChange={(event) => onChange({ ...content, mode: event.target.value as ErrorCorrectionContent['mode'] })}
      >
        <MenuItem value="click">{t('exercises.editor.errorModeClick')}</MenuItem>
        <MenuItem value="click_and_fix">{t('exercises.editor.errorModeClickAndFix')}</MenuItem>
      </TextField>

      {content.mode === 'click_and_fix' && (
        <CommaListField
          label={t('exercises.editor.corrections')}
          helperText={t('exercises.editor.correctionsHint')}
          values={content.corrections ?? []}
          disabled={disabled}
          onChange={(corrections) => onChange({ ...content, corrections })}
        />
      )}
    </Stack>
  )
}

export function TranslationEditor({ content, onChange, disabled }: EditorProps<TranslationContent>) {
  const { t } = useTranslation()

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        label={t('exercises.editor.sourceSentence')}
        value={content.source}
        disabled={disabled}
        onChange={(event) => onChange({ ...content, source: event.target.value })}
      />

      <TextField
        select
        size="small"
        label={t('exercises.editor.direction')}
        value={content.direction}
        disabled={disabled}
        onChange={(event) => onChange({ ...content, direction: event.target.value as TranslationContent['direction'] })}
      >
        <MenuItem value="ru-en">{t('exercises.editor.directionRuEn')}</MenuItem>
        <MenuItem value="en-ru">{t('exercises.editor.directionEnRu')}</MenuItem>
      </TextField>

      <StringListEditor
        label={t('exercises.editor.acceptedTranslations')}
        values={content.accepted ?? ['']}
        onChange={(accepted) => onChange({ ...content, accepted })}
        addLabel={t('exercises.editor.addTranslation')}
        multiline
        disabled={disabled}
      />

      <Typography variant="caption" color="text.secondary">
        {t('exercises.editor.translationHint')}
      </Typography>
    </Stack>
  )
}

export function OpenAnswerEditor({ content, onChange, disabled }: EditorProps<OpenAnswerContent>) {
  const { t } = useTranslation()

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        multiline
        minRows={3}
        size="small"
        label={t('exercises.editor.prompt')}
        value={content.prompt}
        disabled={disabled}
        onChange={(event) => onChange({ ...content, prompt: event.target.value })}
      />
      <TextField
        size="small"
        type="number"
        label={t('exercises.editor.minWords')}
        sx={{ maxWidth: 220 }}
        inputProps={{ min: 1 }}
        value={content.min_words ?? ''}
        disabled={disabled}
        onChange={(event) =>
          onChange({ ...content, min_words: event.target.value === '' ? null : Number(event.target.value) })
        }
      />
      <Typography variant="caption" color="text.secondary">
        {t('exercises.editor.teacherCheckedHint')}
      </Typography>
    </Stack>
  )
}

export function OralAnswerEditor({ content, onChange, disabled }: EditorProps<OralAnswerContent>) {
  const { t } = useTranslation()

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        multiline
        minRows={3}
        size="small"
        label={t('exercises.editor.prompt')}
        value={content.prompt}
        disabled={disabled}
        onChange={(event) => onChange({ ...content, prompt: event.target.value })}
      />
      <TextField
        size="small"
        type="number"
        label={t('exercises.editor.maxDuration')}
        sx={{ maxWidth: 220 }}
        inputProps={{ min: 5, max: 600 }}
        value={content.max_duration_sec ?? ''}
        disabled={disabled}
        onChange={(event) =>
          onChange({ ...content, max_duration_sec: event.target.value === '' ? null : Number(event.target.value) })
        }
      />
      <Typography variant="caption" color="text.secondary">
        {t('exercises.editor.oralAnswerHint')}
      </Typography>
    </Stack>
  )
}
